import React from 'react';
import PropTypes from 'prop-types';

import { useAuth } from 'hooks/useAuth';

import {
  Checkbox,
  Combobox,
  Group,
  Input,
  Pill,
  PillsInput,
  ScrollAreaAutosize,
  Stack,
  Tree,
  useCombobox,
  Flex,
} from '@mantine/core';
import { ChevronDownIconSm } from '@chownow/pantry/icons';

const COMPANY_PREFIX = 'company-';

export function buildMerchantOptions(merchants) {
  return Object.values(merchants).map(({ name, id, companyID }) => ({
    title: name || id,
    value: id,
    key: id,
    companyID,
  }));
}

export function filterSelectableMerchants(merchantOptions, menu) {
  return menu?.merchantId
    ? merchantOptions.filter(m => m.key.toString() !== menu.merchantId.toString())
    : merchantOptions;
}

export function buildTreeData(companies, selectableMerchantOptions) {
  const treeData = [];
  Object.values(companies).forEach(({ name: companyName, id }) => {
    const children = selectableMerchantOptions
      .filter(({ companyID }) => companyID.toString() === id.toString())
      .map(merchant => ({
        label: merchant.title,
        value: merchant.value.toString(),
        key: merchant.key,
        indent: 1,
      }));
    if (children.length > 0) {
      treeData.push({
        label: companyName,
        value: `${COMPANY_PREFIX}${id}`,
        key: `${COMPANY_PREFIX}${id}`,
        children,
      });
    }
  });
  return treeData;
}

export function computeSelectableState(selectableMerchantOptions, selectedMerchants) {
  const ids = selectableMerchantOptions.map(m => m.value.toString());
  const allChecked = ids.length > 0 && ids.every(el => selectedMerchants.includes(el));
  const anyChecked = ids.some(el => selectedMerchants.includes(el));
  // Intentionally uses anyChecked (not isEmpty(selectedMerchants)) so that
  // filtered-out merchants (e.g. current merchant) don't cause indeterminate state.
  return { allChecked, indeterminate: anyChecked && !allChecked };
}

export default function MerchantTreeSelect({
  disabled,
  label,
  menu,
  required,
  selectedMerchants,
  setSelectedMerchants,
  width,
}) {
  const { merchants, companies } = useAuth();

  const combobox = useCombobox();

  const merchantOptions = buildMerchantOptions(merchants);

  const selectableMerchantOptions = filterSelectableMerchants(merchantOptions, menu);

  const treeData = buildTreeData(companies, selectableMerchantOptions);

  const arrayOfMerchantIds = selectableMerchantOptions.map(merchant => merchant.value.toString());
  const { allChecked, indeterminate: indeterminateAll } = computeSelectableState(
    selectableMerchantOptions,
    selectedMerchants,
  );

  const handleValueSelect = val => {
    if (val === 'all') {
      if (allChecked) {
        setSelectedMerchants([]);
      } else {
        setSelectedMerchants(arrayOfMerchantIds);
      }
    } else if (val.includes(COMPANY_PREFIX)) {
      const companyId = val.split('-').pop();
      const merchantsForCompany = selectableMerchantOptions.filter(
        merchant => merchant.companyID.toString() === companyId,
      );
      const arrayOfUpdatedMerchantIds = merchantsForCompany.map(merchant =>
        merchant.value.toString(),
      );
      setSelectedMerchants(merchantValueArray => {
        if (arrayOfUpdatedMerchantIds.every(element => merchantValueArray.includes(element))) {
          return merchantValueArray.filter(v => !arrayOfUpdatedMerchantIds.includes(v));
        }
        return [...merchantValueArray, ...arrayOfUpdatedMerchantIds];
      });
    } else {
      setSelectedMerchants(merchantValueArray => {
        if (merchantValueArray.includes(val)) {
          return merchantValueArray.filter(v => v !== val);
        }

        return [...merchantValueArray, val];
      });
    }
  };

  const selectedOptions = merchantOptions.filter(merchant =>
    selectedMerchants.includes(merchant.value.toString()),
  );

  // Handle merchant IDs that aren't in merchantOptions (user doesn't have access to them)
  const selectedMerchantIdSet = new Set(selectedOptions.map(m => m.value.toString()));
  const missingMerchantIds = selectedMerchants.filter(
    id => !selectedMerchantIdSet.has(id.toString()),
  );
  const missingMerchantOptions = missingMerchantIds.map(id => ({
    value: id,
    title: id.toString(),
    key: id,
  }));
  const allSelectedOptions = [...selectedOptions, ...missingMerchantOptions];

  const displayNames = !allSelectedOptions.length ? (
    <Input.Placeholder>Select a merchant</Input.Placeholder>
  ) : (
    allSelectedOptions.map(merchant => (
      <Pill
        radius="xs2"
        key={merchant.value}
        withRemoveButton
        onRemove={() => handleValueSelect(merchant.value.toString())}
      >
        {merchant.title}
      </Pill>
    ))
  );

  const renderTreeNode = ({ node, expanded, hasChildren, tree }) => {
    const { indent, key, label, value } = node;

    function getArrayofMerchantIdForCompany(selectedValue) {
      const companyId = selectedValue.split('-').pop();
      return selectableMerchantOptions
        .filter(merchant => merchant.companyID.toString() === companyId)
        .map(merchant => merchant.value.toString());
    }

    function getIsChecked() {
      if (value.includes(COMPANY_PREFIX)) {
        const arrayOfUpdatedMerchantIds = getArrayofMerchantIdForCompany(value);
        return arrayOfUpdatedMerchantIds.every(element => selectedMerchants.includes(element));
      }

      return selectedMerchants.includes(value);
    }

    function getIsIndeterminate() {
      if (!value.includes(COMPANY_PREFIX)) return false; // merchants never show indeterminate

      const arrayOfUpdatedMerchantIds = getArrayofMerchantIdForCompany(value);

      // checked merchant values includes some but not all
      return (
        arrayOfUpdatedMerchantIds.some(el => selectedMerchants.includes(el)) &&
        !arrayOfUpdatedMerchantIds.every(el => selectedMerchants.includes(el))
      );
    }

    return (
      <Combobox.Option value={value} key={key} active={selectedMerchants.includes(value)}>
        <Group gap="sm" pl={indent * 32} justify="space-between">
          <Checkbox
            readOnly
            checked={getIsChecked()}
            indeterminate={getIsIndeterminate()}
            aria-hidden
            tabIndex={-1}
            style={{ pointerEvents: 'none' }}
            label={label}
          />
          {hasChildren && (
            <ChevronDownIconSm
              size={14}
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              onClick={e => {
                e.stopPropagation();
                tree.toggleExpanded(node.value);
              }}
            />
          )}
        </Group>
      </Combobox.Option>
    );
  };

  return (
    <Stack maw={width}>
      <Combobox store={combobox} onOptionSubmit={handleValueSelect} disabled={disabled}>
        <Combobox.DropdownTarget>
          <PillsInput
            label={label}
            required={required}
            disabled={disabled}
            onClick={() => combobox.openDropdown()}
            styles={{ input: { cursor: 'pointer' } }}
            rightSection={
              <Flex
                align="center"
                justify="center"
                style={{
                  transform: combobox.dropdownOpened ? 'scaleY(-1)' : 'none',
                }}
              >
                <ChevronDownIconSm />
              </Flex>
            }
            rightSectionPointerEvents="none"
          >
            <Pill.Group>{displayNames}</Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>
        <Combobox.Dropdown maw={width} p="sm">
          <ScrollAreaAutosize
            type="scroll"
            mah={400}
            styles={{ content: { display: 'block' } }}
          >
            <Combobox.Group>
              <Combobox.Option value="all">
                <Checkbox
                  readOnly
                  checked={allChecked}
                  indeterminate={indeterminateAll}
                  aria-hidden
                  tabIndex={-1}
                  style={{ pointerEvents: 'none' }}
                  label="All Locations"
                />
              </Combobox.Option>
              <Combobox.Options>
                <Tree
                  data={treeData}
                  levelOffset={23}
                  expandOnClick={false}
                  renderNode={renderTreeNode}
                />
              </Combobox.Options>
            </Combobox.Group>
          </ScrollAreaAutosize>
        </Combobox.Dropdown>
      </Combobox>
    </Stack>
  );
}

MerchantTreeSelect.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string,
  menu: PropTypes.shape({
    merchantId: PropTypes.number,
  }),
  required: PropTypes.bool,
  selectedMerchants: PropTypes.arrayOf(PropTypes.string),
  setSelectedMerchants: PropTypes.func,
  width: PropTypes.number,
};
